import AssessmentPlanSection from '@/components/AssessmentPlanSection';
import CcHpiSection from '@/components/CcHpiSection';
import ContactLensesSection from '@/components/ContactLensesSection';
import ExternalSection from '@/components/ExternalSection';
import HistoriesSection from '@/components/HistoriesSection';
import InternalSection from '@/components/InternalSection';
import KeratometrySection from '@/components/KeratometrySection';
import RefractionSection from '@/components/RefractionSection';
import GlassCard from '@/components/ui/glass-card';
import SuccessDialog from '@/components/ui/success-dialog';
import { useCloudFunction } from '@/hooks/use-cloud-function';
import {
  mapBackendToDisplayName,
  ScribeSection,
  ScribeSectionContent,
  useScribeSectionsEnhanced,
} from '@/hooks/use-scribe-sections-enhanced';
import { useToast } from '@/hooks/use-toast';
import { type AiScribeJobSection } from '@/lib/ai-scribe-service';
import envConfig from '@/lib/env-config';
import {
  AuthStateSelector,
  IntegrationStateSelector,
  SelectedLocationStateSelector,
} from '@/store/selectors';
import { type SimplifiedAppointment } from '@/types/appointment';
import { motion } from 'framer-motion';
import { Brain, CheckCircle, Cpu, Edit3, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from './ui/button';

// Configurable Section Order Configuration
//
// HOW TO CUSTOMIZE SECTION ORDER:
// 1. Reorder the 'order' array to change the display sequence
// 2. Add new section names to the 'order' array
// 3. Add matching patterns to the 'patterns' object for automatic detection
// 4. Add JSON validation logic to the 'jsonValidation' object
// 5. Add component mapping to the 'components' object
//
// EXAMPLE: To add a new "Medications" section:
// 1. Add 'Medications' to the order array where you want it to appear
// 2. Add patterns: 'Medications': ['medication', 'meds', 'drugs']
// 3. Add validation: 'Medications': (json) => json && json.medications !== undefined
// 4. Add component: 'Medications': MedicationsSection
//
const SECTION_ORDER_CONFIG = {
  // Define the order of sections (lower numbers = higher priority)
  order: [
    'CC & HPI',
    'Refraction',
    'Contact Lenses',
    'Internal',
    'External',
    'Keratometry',
    'Histories',
    'Assessment & Plan',
    // Add new sections here as needed
    // 'New Section Name',
  ],

  // Define section matching patterns for automatic detection
  patterns: {
    'CC & HPI': [
      'cc',
      'hpi',
      'cc_hpi',
      'cchpi',
      'cc&hpi',
      'chief complaint',
      'history of present illness',
      'chief complaints',
      'presenting complaint',
    ],
    Refraction: ['refraction', 'rx', 'prescription', 'optical', 'lensometry'],
    'Contact Lenses': [
      'contact',
      'contacts',
      'contact lens',
      'contact lenses',
      'contactlens',
      'contactlenses',
      'cl',
      'lens',
      'lenses',
      'soft lens',
      'trial lens',
      'habitual soft',
      'final soft',
      'habitualsoft',
      'finalsoft',
    ],
    Internal: [
      'internal',
      'fundus',
      'retina',
      'ophthalmoscopy',
      'fundoscopic',
      'posterior segment',
    ],
    External: [
      'external',
      'extern',
      'examination',
      'anterior segment',
      'slit lamp',
      'biomicroscopy',
    ],
    Keratometry: [
      'keratometry',
      'k',
      'corneal curvature',
      'cornea',
      'k readings',
      'keratometer',
    ],
    Histories: [
      'histories',
      'history',
      'ocular history',
      'medical history',
      'family history',
      'social history',
      'ocularhistory',
      'medicalhistory',
      'familyhistory',
      'socialhistory',
    ],
    'Assessment & Plan': [
      'assessment & plan',
      'assessment_plan',
      'assessment plan',
      'assessmentplan',
      'dxplan',
      'dx plan',
      'diagnosis',
      'assessment',
      'plan',
      'a&p',
    ],
    // Add new section patterns here
    // 'New Section Name': ['pattern1', 'pattern2'],
    //
    // EXAMPLE: Adding a Medications section
    // 'Medications': ['medication', 'meds', 'drugs', 'prescription'],
  },

  // Define JSON structure validation for each section type
  jsonValidation: {
    'CC & HPI': (jsonContent: any) => {
      if (!jsonContent) return false;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        const firstItem = jsonContent[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          const firstDataItem = firstItem.data[0];
          return (
            firstDataItem &&
            (firstDataItem.reasonForVisit !== undefined ||
              firstDataItem.ccHpiHpiKey !== undefined ||
              firstDataItem.ccNameKey !== undefined)
          );
        }
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const firstDataItem = jsonContent.data[0];
        return (
          firstDataItem &&
          (firstDataItem.reasonForVisit !== undefined ||
            firstDataItem.ccHpiHpiKey !== undefined ||
            firstDataItem.ccNameKey !== undefined)
        );
      }

      // Check legacy format
      return (
        jsonContent.ccNameKey !== undefined ||
        jsonContent.ccHpiHpiKey !== undefined ||
        jsonContent.chiefComplaint !== undefined ||
        jsonContent.hpi !== undefined
      );
    },
    Refraction: (jsonContent: any) => {
      if (!jsonContent) return false;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        return jsonContent.some(
          (item) =>
            item.testKey &&
            (item.testKey === 'refractions.habitual' ||
              item.testKey === 'refractions.manifest' ||
              item.testKey === 'refractions.final' ||
              item.testKey === 'refractions.autorefractions')
        );
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const firstDataItem = jsonContent.data[0];
        return (
          firstDataItem &&
          (firstDataItem.odSph !== undefined ||
            firstDataItem.osSph !== undefined ||
            firstDataItem.odDVA !== undefined ||
            firstDataItem.osDVA !== undefined ||
            firstDataItem.ouDVA !== undefined ||
            firstDataItem.ouNVA !== undefined)
        );
      }
    },
    'Contact Lenses': (jsonContent: any) => {
      if (!jsonContent) return false;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        return jsonContent.some(
          (item) =>
            item.testKey &&
            (item.testKey === 'contactlenses.habitualsoft' ||
              item.testKey === 'contactlenses.trial' ||
              item.testKey === 'contactlenses.finalsoft')
        );
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const firstDataItem = jsonContent.data[0];
        return (
          firstDataItem &&
          (firstDataItem.odSph !== undefined ||
            firstDataItem.osSph !== undefined ||
            firstDataItem.odBC !== undefined ||
            firstDataItem.osBC !== undefined ||
            firstDataItem.odDia !== undefined ||
            firstDataItem.osDia !== undefined ||
            firstDataItem.odDVA !== undefined ||
            firstDataItem.osDVA !== undefined ||
            firstDataItem.ouDVA !== undefined ||
            firstDataItem.ouNVA !== undefined)
        );
      }
    },
    Internal: (jsonContent: any) => {
      if (!jsonContent) return false;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        const firstItem = jsonContent[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          const firstDataItem = firstItem.data[0];
          return (
            firstDataItem &&
            (firstDataItem.ophtalVitreousOD !== undefined ||
              firstDataItem.ophtalDiscOD !== undefined ||
              firstDataItem.ophtalMaculaOD !== undefined ||
              firstDataItem.ophtalVesselsOD !== undefined ||
              firstDataItem.ophtalPeripheryOD !== undefined ||
              firstDataItem.ophtalVitreousOS !== undefined ||
              firstDataItem.ophtalDiscOS !== undefined ||
              firstDataItem.ophtalMaculaOS !== undefined ||
              firstDataItem.ophtalVesselsOS !== undefined ||
              firstDataItem.ophtalPeripheryOS !== undefined ||
              firstDataItem.fundusOD !== undefined ||
              firstDataItem.fundusOS !== undefined)
          );
        }
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const firstDataItem = jsonContent.data[0];
        return (
          firstDataItem &&
          (firstDataItem.ophtalVitreousOD !== undefined ||
            firstDataItem.ophtalDiscOD !== undefined ||
            firstDataItem.ophtalMaculaOD !== undefined ||
            firstDataItem.ophtalVesselsOD !== undefined ||
            firstDataItem.ophtalPeripheryOD !== undefined ||
            firstDataItem.ophtalVitreousOS !== undefined ||
            firstDataItem.ophtalDiscOS !== undefined ||
            firstDataItem.ophtalMaculaOS !== undefined ||
            firstDataItem.ophtalVesselsOS !== undefined ||
            firstDataItem.ophtalPeripheryOS !== undefined ||
            firstDataItem.fundusOD !== undefined ||
            firstDataItem.fundusOS !== undefined)
        );
      }

      // Check legacy format
      return (
        jsonContent.ophtalVitreousOD !== undefined ||
        jsonContent.ophtalDiscOD !== undefined ||
        jsonContent.ophtalMaculaOD !== undefined ||
        jsonContent.ophtalVesselsOD !== undefined ||
        jsonContent.ophtalPeripheryOD !== undefined ||
        jsonContent.ophtalVitreousOS !== undefined ||
        jsonContent.ophtalDiscOS !== undefined ||
        jsonContent.ophtalMaculaOS !== undefined ||
        jsonContent.ophtalVesselsOS !== undefined ||
        jsonContent.ophtalPeripheryOS !== undefined ||
        jsonContent.fundusOD !== undefined ||
        jsonContent.fundusOS !== undefined
      );
    },
    External: (jsonContent: any) => {
      if (!jsonContent) return false;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        const firstItem = jsonContent[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          const firstDataItem = firstItem.data[0];
          return (
            firstDataItem &&
            (firstDataItem.externLidsLashesOD !== undefined ||
              firstDataItem.externCorneaOD !== undefined ||
              firstDataItem.externBulbConjOD !== undefined ||
              firstDataItem.externLidsLashesOS !== undefined ||
              firstDataItem.externCorneaOS !== undefined ||
              firstDataItem.externBulbConjOS !== undefined ||
              firstDataItem.externalOD !== undefined ||
              firstDataItem.externalOS !== undefined)
          );
        }
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const firstDataItem = jsonContent.data[0];
        return (
          firstDataItem &&
          (firstDataItem.externLidsLashesOD !== undefined ||
            firstDataItem.externCorneaOD !== undefined ||
            firstDataItem.externBulbConjOD !== undefined ||
            firstDataItem.externLidsLashesOS !== undefined ||
            firstDataItem.externCorneaOS !== undefined ||
            firstDataItem.externBulbConjOS !== undefined ||
            firstDataItem.externalOD !== undefined ||
            firstDataItem.externalOS !== undefined)
        );
      }

      // Check legacy format
      return (
        jsonContent.externLidsLashesOD !== undefined ||
        jsonContent.externCorneaOD !== undefined ||
        jsonContent.externBulbConjOD !== undefined ||
        jsonContent.externLidsLashesOS !== undefined ||
        jsonContent.externCorneaOS !== undefined ||
        jsonContent.externBulbConjOS !== undefined ||
        jsonContent.externalOD !== undefined ||
        jsonContent.externalOS !== undefined
      );
    },
    Keratometry: (jsonContent: any) => {
      if (!jsonContent) return false;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        const firstItem = jsonContent[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          const firstDataItem = firstItem.data[0];
          return (
            firstDataItem &&
            (firstDataItem.keratometryFlatKOD !== undefined ||
              firstDataItem.keratometrySteepKOD !== undefined ||
              firstDataItem.keratometryAvgKOD !== undefined ||
              firstDataItem.keratometryFlatKOS !== undefined ||
              firstDataItem.keratometrySteepKOS !== undefined ||
              firstDataItem.keratometryAvgKOS !== undefined ||
              firstDataItem.keratometryMethod !== undefined ||
              firstDataItem.keratometryMireQuality !== undefined)
          );
        }
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const firstDataItem = jsonContent.data[0];
        return (
          firstDataItem &&
          (firstDataItem.keratometryFlatKOD !== undefined ||
            firstDataItem.keratometrySteepKOD !== undefined ||
            firstDataItem.keratometryAvgKOD !== undefined ||
            firstDataItem.keratometryFlatKOS !== undefined ||
            firstDataItem.keratometrySteepKOS !== undefined ||
            firstDataItem.keratometryAvgKOS !== undefined ||
            firstDataItem.keratometryMethod !== undefined ||
            firstDataItem.keratometryMireQuality !== undefined)
        );
      }

      // Check if it's the nested structure with pages[0].data
      if (
        jsonContent.pages &&
        jsonContent.pages[0] &&
        jsonContent.pages[0].data
      ) {
        const data = jsonContent.pages[0].data;
        return (
          data.keratometryFlatKOD !== undefined ||
          data.keratometrySteepKOD !== undefined ||
          data.keratometryAvgKOD !== undefined ||
          data.keratometryFlatKOS !== undefined ||
          data.keratometrySteepKOS !== undefined ||
          data.keratometryAvgKOS !== undefined ||
          data.keratometryMethod !== undefined ||
          data.keratometryMireQuality !== undefined
        );
      }

      // Check if it's the flat structure
      return (
        jsonContent.keratometryFlatKOD !== undefined ||
        jsonContent.keratometrySteepKOD !== undefined ||
        jsonContent.keratometryAvgKOD !== undefined ||
        jsonContent.keratometryFlatKOS !== undefined ||
        jsonContent.keratometrySteepKOS !== undefined ||
        jsonContent.keratometryAvgKOS !== undefined ||
        jsonContent.keratometryMethod !== undefined ||
        jsonContent.keratometryMireQuality !== undefined
      );
    },
    Histories: (jsonContent: any) => {
      if (!jsonContent) return false;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        return jsonContent.some(
          (item) =>
            item.testKey &&
            (item.testKey === 'ocularhistory' ||
              item.testKey === 'medicalhistory' ||
              item.testKey === 'familyhistory' ||
              item.testKey === 'socialhistory' ||
              item.testKey === 'ocularhistory.surgery' ||
              item.testKey === 'medicalhistory.surgery' ||
              item.testKey === 'medicalhistory.injury' ||
              item.testKey === 'socialhistory.hobby')
        );
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const firstDataItem = jsonContent.data[0];
        return (
          firstDataItem &&
          (firstDataItem.ocularHistoryEyeInfectionComment !== undefined ||
            firstDataItem.ocularHistoryInjuryState !== undefined ||
            firstDataItem.medicalHistoryLastMedicalExam !== undefined ||
            firstDataItem.medicalHistoryHeartDiseaseState !== undefined ||
            firstDataItem.familyHistorySister !== undefined ||
            firstDataItem.familyHistoryFather !== undefined ||
            firstDataItem.socialHistoryMaritalStatus !== undefined ||
            firstDataItem.socialHistoryComputerUse !== undefined)
        );
      }

      // Check legacy format with businessKey
      if (Array.isArray(jsonContent)) {
        return jsonContent.some(
          (item) =>
            item.businessKey &&
            (item.businessKey === 'test.ocularhistory' ||
              item.businessKey === 'test.medicalhistory' ||
              item.businessKey === 'test.familyhistory' ||
              item.businessKey === 'test.socialhistory')
        );
      }

      // Check if it's the nested structure with pages[0].data
      if (
        jsonContent.pages &&
        jsonContent.pages[0] &&
        jsonContent.pages[0].data
      ) {
        const data = jsonContent.pages[0].data;
        return (
          data.ocularHistoryEyeInfectionComment !== undefined ||
          data.ocularHistoryInjuryState !== undefined ||
          data.medicalHistoryLastMedicalExam !== undefined ||
          data.medicalHistoryHeartDiseaseState !== undefined ||
          data.familyHistorySister !== undefined ||
          data.familyHistoryFather !== undefined ||
          data.socialHistoryMaritalStatus !== undefined ||
          data.socialHistoryComputerUse !== undefined
        );
      }

      // Check if it's the flat structure
      return (
        jsonContent.ocularHistoryEyeInfectionComment !== undefined ||
        jsonContent.ocularHistoryInjuryState !== undefined ||
        jsonContent.medicalHistoryLastMedicalExam !== undefined ||
        jsonContent.medicalHistoryHeartDiseaseState !== undefined ||
        jsonContent.familyHistorySister !== undefined ||
        jsonContent.familyHistoryFather !== undefined ||
        jsonContent.socialHistoryMaritalStatus !== undefined ||
        jsonContent.socialHistoryComputerUse !== undefined
      );
    },
    'Assessment & Plan': (jsonContent: any) => {
      if (!jsonContent) return false;

      const matchEnvelope = (env: any) => {
        if (!env || typeof env !== 'object') return false;
        const tk = String(env.testKey || '').toLowerCase();
        if (!tk) return false;
        if (
          tk.includes('assessmentplan') ||
          (tk.includes('assessment') && tk.includes('plan')) ||
          tk.includes('assessment.plan') ||
          tk.includes('plan.assessment') ||
          tk.includes('plan.details') ||
          tk.includes('referral') ||
          tk.includes('follow') ||
          tk.includes('handoff') ||
          // Specific check for assessmentFindings.Summary
          (tk.includes('assessmentfindings') && tk.includes('summary')) ||
          // Specific check for dxplan.diagnosis
          (tk.includes('dxplan') && tk.includes('diagnosis')) ||
          (env.data &&
            Array.isArray(env.data) &&
            env.data.length > 0 &&
            (env.data[0].assessment !== undefined ||
              env.data[0].icd10Code !== undefined ||
              env.data[0].planNote !== undefined ||
              env.data[0].finding !== undefined ||
              env.data[0].summary !== undefined ||
              env.data[0].dx !== undefined))
        ) {
          return true;
        }
        return false;
      };
      if (Array.isArray(jsonContent)) {
        return jsonContent.some((env) => matchEnvelope(env));
      }

      if (
        typeof jsonContent === 'object' &&
        'testKey' in jsonContent &&
        'data' in jsonContent
      ) {
        return matchEnvelope(jsonContent);
      }

      if (
        typeof jsonContent === 'object' &&
        (jsonContent.assessments !== undefined ||
          jsonContent.assessmentFindings !== undefined ||
          jsonContent.detailsByType !== undefined)
      ) {
        return true;
      }

      return false;
    },
    // Add new section validation here
    // 'New Section Name': (jsonContent: any) => jsonContent && (/* validation logic */),
    //
    // EXAMPLE: Adding Medications validation
    // 'Medications': (jsonContent: any) => jsonContent && (
    //   jsonContent.currentMedications !== undefined ||
    //   jsonContent.medicationList !== undefined ||
    //   jsonContent.drugs !== undefined
    // ),
  },

  // Define component mapping for each section type
  components: {
    'CC & HPI': CcHpiSection,
    Refraction: RefractionSection,
    'Contact Lenses': ContactLensesSection,
    Keratometry: KeratometrySection,
    Internal: InternalSection,
    External: ExternalSection,
    Histories: HistoriesSection,
    'Assessment & Plan': AssessmentPlanSection,
    // Add new section components here
    // 'New Section Name': NewSectionComponent,
    //
    // EXAMPLE: Adding Medications component
    // 'Medications': MedicationsSection,
  },
} as const;

// Helper function to determine section type based on name and content
const getSectionType = (
  sectionName: string,
  jsonContent: any
): keyof typeof SECTION_ORDER_CONFIG.components | null => {
  const normalizedName = sectionName.toLowerCase();

  // Remove verbose CC_HPI debug logs

  for (const [sectionType, patterns] of Object.entries(
    SECTION_ORDER_CONFIG.patterns
  )) {
    if (patterns.some((pattern) => normalizedName.includes(pattern))) {
      // Additional validation using JSON structure if available
      const validator =
        SECTION_ORDER_CONFIG.jsonValidation[
          sectionType as keyof typeof SECTION_ORDER_CONFIG.jsonValidation
        ];

      // If we have content, validate it. If not, trust the name pattern match
      if (!jsonContent || !validator || validator(jsonContent)) {
        // no verbose logs
        return sectionType as keyof typeof SECTION_ORDER_CONFIG.components;
      }
    }
  }

  // Fallback: try to infer by JSON content alone if name patterns didn't match
  if (jsonContent) {
    for (const [sectionType, validator] of Object.entries(
      SECTION_ORDER_CONFIG.jsonValidation
    )) {
      try {
        if (validator && validator(jsonContent)) {
          return sectionType as keyof typeof SECTION_ORDER_CONFIG.components;
        }
      } catch {
        // ignore validator errors
      }
    }
  }

  // no verbose logs

  return null;
};

// Helper function to sort sections by configured order
const sortSectionsByOrder = (
  sections: Array<{ sectionName: string; [key: string]: any }>
) => {
  return [...sections].sort((a, b) => {
    const aType = getSectionType(a.sectionName, a.jsonContent);
    const bType = getSectionType(b.sectionName, b.jsonContent);

    const aOrder = aType ? SECTION_ORDER_CONFIG.order.indexOf(aType) : -1;
    const bOrder = bType ? SECTION_ORDER_CONFIG.order.indexOf(bType) : -1;

    // If both have defined order, sort by order
    if (aOrder !== -1 && bOrder !== -1) {
      return aOrder - bOrder;
    }

    // If only one has defined order, prioritize it
    if (aOrder !== -1) return -1;
    if (bOrder !== -1) return 1;

    // If neither has defined order, maintain original order
    return 0;
  });
};

// Enhanced Section Card Component for Real-time Sections
const SectionCard = ({
  sectionName,
  content,
  isLoading,
  error,
  index,
  onAccept,
  onClick,
}: {
  sectionName: string;
  content: string;
  isLoading: boolean;
  error: string | null;
  index: number;
  onAccept?: () => void;
  onClick?: (event: React.MouseEvent) => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
      className="group mb-6">
      {/* Enhanced card with subtle border and spacing */}
      <div
        className={`relative bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 rounded-3xl p-6 transition-all duration-300 ease-out hover:scale-[1.01] hover:-translate-y-0.5 ${
          onClick ? 'cursor-pointer' : ''
        }`}
        onClick={onClick}>
        {/* Enhanced inner highlight with better gradient */}
        <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-br from-white/30 via-transparent to-white/10 dark:from-white/10 dark:via-transparent dark:to-white/5 pointer-events-none" />

        {/* Subtle border glow effect */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-eyevantage-primary/10 via-transparent to-eyevantage-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        {/* Content */}
        <div className="relative z-10">
          {/* Enhanced Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-gradient-to-r from-eyevantage-primary to-eyevantage-secondary rounded-full shadow-sm" />
              <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-lg tracking-wide">
                {sectionName}
              </h3>
              {isLoading && (
                <div className="flex items-center gap-2 ml-3">
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    className="w-2 h-2 bg-gradient-to-r from-eyevantage-primary to-purple-500 rounded-full"
                  />
                  <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                    Fetching data...
                  </span>
                </div>
              )}
            </div>
            {(!error || error !== 'Data not found') &&
              content &&
              !isLoading && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="relative h-9 px-4 bg-white/75 hover:bg-white/90 dark:bg-slate-800/75 dark:hover:bg-slate-700/90 border border-white/40 dark:border-white/20 text-slate-700 dark:text-slate-200 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-md shadow-sm hover:shadow-md flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-500/40 active:scale-[0.98] hover:scale-[1.02] overflow-hidden">
                    {/* Subtle glass layer */}
                    <div className="absolute inset-[1px] rounded-lg bg-gradient-to-br from-white/20 via-white/10 to-white/5 dark:from-white/10 dark:via-white/5 dark:to-transparent" />

                    <div className="relative z-10 flex items-center gap-2">
                      <Edit3 className="w-4 h-4" />
                      Edit
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAccept?.();
                    }}
                    className="relative h-9 px-4 bg-gradient-to-r from-emerald-500/85 to-emerald-600/85 hover:from-emerald-500 hover:to-emerald-600 border border-emerald-400/40 text-white rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-md shadow-sm hover:shadow-md shadow-emerald-500/20 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-500/40 active:scale-[0.98] hover:scale-[1.02] overflow-hidden">
                    {/* Subtle glass layer */}
                    <div className="absolute inset-[1px] rounded-lg bg-gradient-to-br from-white/20 via-white/10 to-white/5" />

                    <div className="relative z-10 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Accept
                    </div>
                  </button>
                </div>
              )}
          </div>

          {/* Enhanced Content */}
          <div className="prose prose-sm max-w-none">
            {error ? (
              <div className="text-slate-500 dark:text-slate-400 text-sm italic bg-slate-50/50 dark:bg-slate-800/30 rounded-xl p-4 border border-slate-200/30 dark:border-slate-700/30">
                {error === 'Data not found' ? (
                  `No ${sectionName.toLowerCase()} data available yet`
                ) : (
                  <div className="text-red-600 dark:text-red-400">
                    <span className="font-medium">Error:</span> {error}
                  </div>
                )}
              </div>
            ) : isLoading ? (
              <div className="relative overflow-hidden bg-slate-50/50 dark:bg-slate-800/30 rounded-xl p-6 border border-slate-200/30 dark:border-slate-700/30">
                {/* Shimmer effect background */}
                <motion.div
                  animate={{
                    x: ['-100%', '100%'],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                />

                <div className="relative z-10 flex items-center gap-4">
                  {/* Animated loading dots */}
                  <div className="flex items-center gap-1">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.4, 1, 0.4],
                        }}
                        transition={{
                          duration: 1.2,
                          repeat: Infinity,
                          ease: 'easeInOut',
                          delay: i * 0.2,
                        }}
                        className="w-2 h-2 bg-gradient-to-r from-eyevantage-primary to-purple-500 rounded-full"
                      />
                    ))}
                  </div>

                  <div className="flex-1">
                    <div className="h-4 bg-slate-200/50 dark:bg-slate-700/50 rounded mb-2" />
                    <div className="h-3 bg-slate-200/30 dark:bg-slate-700/30 rounded w-3/4" />
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="h-3 bg-slate-200/40 dark:bg-slate-700/40 rounded" />
                  <div className="h-3 bg-slate-200/30 dark:bg-slate-700/30 rounded w-5/6" />
                  <div className="h-3 bg-slate-200/40 dark:bg-slate-700/40 rounded w-4/5" />
                </div>
              </div>
            ) : content ? (
              <div className="bg-slate-50/30 dark:bg-slate-800/20 rounded-xl p-4 border border-slate-200/20 dark:border-slate-700/20">
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line text-sm break-words">
                  {content}
                </p>
              </div>
            ) : (
              <div className="text-slate-500 dark:text-slate-400 text-sm italic bg-slate-50/50 dark:bg-slate-800/30 rounded-xl p-4 border border-slate-200/30 dark:border-slate-700/30">
                No content available yet {isLoading ? 'loading' : 'not loading'}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Placeholder Component for No EHR Documentation
const NoInsightsPlaceholder = () => {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        minHeight: 'calc(100vh - 25.4rem)',
      }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="text-center max-w-md px-4">
        <div className="flex items-center justify-center mx-auto mb-4">
          <Brain className="w-40 h-40 text-slate-200 dark:text-slate-500" />
        </div>
        <h3 className="text-slate-400 dark:text-slate-300 mb-6 text-2xl font-light">
          No EHR Documentation generated yet
        </h3>
        <p className="text-slate-400 dark:text-slate-400 text-md font-light">
          AI will analyze your conversation after recording to provide
          structured EHR documentation, clinical insights, and recommendations.
        </p>
      </motion.div>
    </div>
  );
};

const AiProcessingLoader = () => {
  return (
    <div className="flex items-center justify-center py-16 relative">
      {/* Subtle background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-radial from-eyevantage-primary/5 to-transparent rounded-full" />
      </div>

      <motion.div className="flex flex-col items-center space-y-8 max-w-lg relative z-10">
        {/* Clean AI Brain Icon */}
        <div className="relative">
          <motion.div
            className="relative w-20 h-20 bg-gradient-to-br from-eyevantage-primary/10 to-eyevantage-secondary/10 rounded-2xl flex items-center justify-center border border-white/20 dark:border-white/10 backdrop-blur-md shadow-md"
            animate={{
              scale: [1, 1.02, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}>
            <Cpu className="w-12 h-12 text-eyevantage-primary" />
          </motion.div>
        </div>

        {/* Clean Text Layout */}
        <div className="text-center space-y-6">
          <div>
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
              className="text-3xl font-medium tracking-tight relative"
              style={{
                background:
                  'linear-gradient(90deg, #64748b, #8b5cf6, #06b6d4, #64748b)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
              <motion.span
                animate={{
                  backgroundPosition: ['0% 50%', '200% 50%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                  delay: 1,
                }}
                style={{
                  background:
                    'linear-gradient(90deg, #64748b, #8b5cf6, #06b6d4, #64748b)',
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'inline-block',
                }}>
                EyeVantage Ai
              </motion.span>
            </motion.h3>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6, ease: 'easeOut' }}
              className="mt-2 text-md font-medium text-slate-600 dark:text-slate-400">
              Generating EHR Documentation
            </motion.div>
          </div>

          {/* Continuously Moving Progress Bar */}
          <div className="relative max-w-sm mx-auto">
            <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              {/* Continuously moving progress indicator */}
              <motion.div
                className="h-full w-1/3 bg-gradient-to-r from-eyevantage-primary to-eyevantage-secondary rounded-full"
                animate={{
                  x: ['-100%', '400%'],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              />
            </div>
          </div>

          {/* Refined Description */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8, ease: 'easeOut' }}
            className="text-slate-600 dark:text-slate-400 text-md leading-relaxed max-w-sm font-medium">
            AI is analyzing your conversation and creating structured clinical
            documentation. This process typically takes 15-30 seconds.
          </motion.p>

          {/* Simple Processing Steps */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: 'easeOut' }}
            className="flex items-center space-x-6">
            {[
              { label: 'Processing', color: 'bg-eyevantage-accent', delay: 0 },
              {
                label: 'Transcribing',
                color: 'bg-eyevantage-primary',
                delay: 0.5,
              },
              {
                label: 'Generating',
                color: 'bg-eyevantage-secondary',
                delay: 1.0,
              },
            ].map((step, index) => (
              <div key={index} className="flex items-center space-x-2">
                <motion.div
                  animate={{
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: step.delay,
                  }}
                  className={`w-2 h-2 ${step.color} rounded-full`}
                />
                <span className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                  {step.label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

interface AiScribeInsightsProps {
  selectedAppointment: SimplifiedAppointment | null;
  jobDetails: any;
  aiScribeLoading: boolean;
  isTranscriptVisible: boolean;
  scribeJobId?: string; // Add scribe job ID prop for sections subscription
  realtimeEnabled?: boolean; // Control realtime subscriptions based on recording state
  selectedSection?: string;
  onHighlightSegments?: (segmentIds: string[]) => void; // New prop for highlighting segments
  onSelectedSection?: (sectionName: string) => void; // New prop for selecting sections
}

// API Section Content interface
interface ApiSectionContent {
  sectionName: string;
  content: string;
  jsonContent: any | null; // Parsed JSON content for editing
  loading: boolean;
  error: string | null;
}

const AiScribeInsights = ({
  selectedAppointment,
  jobDetails,
  aiScribeLoading,
  isTranscriptVisible,
  scribeJobId,
  realtimeEnabled = false, // Default to false for safety
  selectedSection,
  onHighlightSegments,
  onSelectedSection,
}: AiScribeInsightsProps) => {
  const { toast } = useToast();
  const { makeCallToIntegrationPartner } = useCloudFunction();

  // Get processing state from Redux
  const selectedLocationId = useSelector(
    SelectedLocationStateSelector.locationId
  );
  const integrationToken = useSelector(AuthStateSelector.integrationToken);
  const hasValidToken = useSelector(AuthStateSelector.isAuthenticated);
  const endpoints = useSelector(IntegrationStateSelector.endpoints);

  // State for API sections content
  const [apiSectionContents, setApiSectionContents] = useState<
    Record<string, ApiSectionContent>
  >({});
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);

  // State for success dialog
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [hasShownSuccessDialog, setHasShownSuccessDialog] = useState(false);

  // Get the effective scribe job ID from props or fallback to component data
  const effectiveScribeJobId =
    scribeJobId || selectedAppointment?.scribeJobId || jobDetails?.job?.id;

  // Get API sections from job details (for display only, not for content fetching)
  const apiSections: AiScribeJobSection[] = jobDetails?.job?.sections || [];

  // Add this after your existing state declarations
  const fetchingSectionsRef = React.useRef<Set<string>>(new Set());

  // Hook for Realtime Sections
  const {
    scribeSections,
    isLoadingAnyContent: isRealtimeLoading,
    contentErrors: realtimeContentErrors,
    getSectionContent,
    clearAllContent,
    allExpectedSectionsLoaded,
    expectedSectionsCount,
    loadedSectionsCount,
  } = useScribeSectionsEnhanced({
    scribeJobId: effectiveScribeJobId,
    enabled: realtimeEnabled && !!effectiveScribeJobId,
    disableCompletionToast: true, // Disable hook's toast since we show success dialog
  });

  // Log content fetching errors only
  useEffect(() => {
    if (realtimeContentErrors.length > 0) {
      console.error(
        '❌ Section content fetching errors:',
        realtimeContentErrors
      );
    }
  }, [realtimeContentErrors]);

  // Clear API section contents when job details change to prevent data crossover
  useEffect(() => {
    if (jobDetails?.job?.id) {
      setApiSectionContents({});
      clearAllContent();
      // Reset success dialog states when starting new job
      setShowSuccessDialog(false);
      setHasShownSuccessDialog(false);
    }
  }, [jobDetails?.job?.id, clearAllContent]);

  // Detect when all API sections are loaded and show success dialog
  const allApiSectionsLoaded = useMemo(() => {
    if (apiSections.length === 0) return false;

    return apiSections.every((section) => {
      const sectionKey = `${section.sectionName}-${section.id}`;
      const content = apiSectionContents[sectionKey];
      return (
        content &&
        !content.loading &&
        !content.error &&
        (content.jsonContent || content.content)
      );
    });
  }, [apiSections, apiSectionContents]);

  // Show success dialog only when realtime sections are completed (when AI generates EHR documentation)
  useEffect(() => {
    // Only show dialog for realtime sections when AI actively generates documentation
    const shouldShowDialog = realtimeEnabled && allExpectedSectionsLoaded;

    if (shouldShowDialog && !hasShownSuccessDialog && selectedAppointment) {
      console.log(
        '✅ Showing success dialog: All sections loaded successfully!'
      );
      setShowSuccessDialog(true);
    }
  }, [
    allExpectedSectionsLoaded,
    hasShownSuccessDialog,
    realtimeEnabled,
    selectedAppointment,
  ]);

  // Resolve endpoint for a given action
  const getEndpointForAction = useCallback(
    (action: string) => {
      // Prefer production by default
      const prod = endpoints.find(
        (ep) => ep.action === action && ep.environment === envConfig.env
      );
      if (prod) return prod;
      // Fallback to any matching action if production not found
      return (
        endpoints.find(
          (ep) => ep.action === action && ep.environment === envConfig.env
        ) || null
      );
    },
    [endpoints]
  );

  // Helpers to normalize section JSON into an array of envelopes: { testKey, data: object[] }
  interface NormalizedEnvelope {
    testKey: string;
    data: any[];
  }

  const inferTestKeyForSection = useCallback((sectionName: string): string => {
    const name = sectionName.toLowerCase();
    if (name.includes('cc') || name.includes('hpi')) return 'cc_hpi';
    if (name.includes('refraction')) return 'refraction';
    if (name.includes('kerat')) return 'keratometry';
    if (
      name.includes('internal') ||
      name.includes('fundus') ||
      name.includes('retina')
    )
      return 'internal';
    if (name.includes('external') || name.includes('slit')) return 'external';
    if (name.includes('history')) return 'histories';
    if (name.includes('assessment') || name.includes('plan'))
      return 'assessmentplan';
    return (
      name.replace(/[^a-z0-9]+/g, '.').replace(/^\.|\.$/g, '') || 'section'
    );
  }, []);

  const ensureArray = (value: any): any[] =>
    Array.isArray(value) ? value : value != null ? [value] : [];

  const wrapAsEnvelope = useCallback(
    (sectionName: string, maybeEnvelope: any): NormalizedEnvelope[] => {
      if (maybeEnvelope == null) return [];

      // If input itself is an array, normalize each item
      if (Array.isArray(maybeEnvelope)) {
        return maybeEnvelope.flatMap((item) =>
          wrapAsEnvelope(sectionName, item)
        );
      }

      // If object with pages[0].data
      if (
        typeof maybeEnvelope === 'object' &&
        (maybeEnvelope as any).pages &&
        Array.isArray((maybeEnvelope as any).pages) &&
        (maybeEnvelope as any).pages[0] &&
        (maybeEnvelope as any).pages[0].data !== undefined
      ) {
        const tk = inferTestKeyForSection(sectionName);
        const data = ensureArray((maybeEnvelope as any).pages[0].data).flat();
        return [{ testKey: tk, data }];
      }

      // If object with { testKey, data }
      if (
        typeof maybeEnvelope === 'object' &&
        ('testKey' in (maybeEnvelope as any) ||
          'data' in (maybeEnvelope as any))
      ) {
        const tk = String(
          (maybeEnvelope as any).testKey || inferTestKeyForSection(sectionName)
        );
        const rawData = (maybeEnvelope as any).data;
        const data =
          rawData !== undefined ? ensureArray(rawData).flat() : [maybeEnvelope];
        return [{ testKey: tk, data }];
      }

      // Plain object -> wrap with inferred testKey
      if (typeof maybeEnvelope === 'object') {
        const tk = inferTestKeyForSection(sectionName);
        return [{ testKey: tk, data: [maybeEnvelope] }];
      }

      // Ignore primitives
      return [];
    },
    [inferTestKeyForSection]
  );

  // Determine if any content is available to enable Accept All
  const hasAnyContent = useMemo(() => {
    // Check API sections
    if (apiSections.length > 0) {
      return apiSections.some((s) => {
        const key = `${s.sectionName}-${s.id}`;
        const content = apiSectionContents[key];
        // Don't show Accept All if there's a "Data not found" error
        if (content?.error === 'Data not found') return false;
        // Show if there's JSON content or text content
        return !!(content?.jsonContent || content?.content);
      });
    }

    // Check realtime sections
    if (scribeSections.length > 0) {
      return scribeSections.some((section) => {
        const content = getSectionContent(section.id);
        // Don't show Accept All if there's a "Data not found" error
        if (content?.error === 'Data not found') return false;
        // Show if there's JSON content or text content
        return !!(content?.jsonContent || content?.textContent);
      });
    }

    return false;
  }, [apiSections, apiSectionContents, scribeSections, getSectionContent]);

  // Collect and flatten JSON from all sections into a single array
  const collectAllSectionJsonEnvelopes =
    useCallback((): NormalizedEnvelope[] => {
      const combined: NormalizedEnvelope[] = [];

      if (apiSections.length > 0) {
        // Follow display priority: API sections first
        apiSections.forEach((s) => {
          const key = `${s.sectionName}-${s.id}`;
          const json = apiSectionContents[key]?.jsonContent;
          if (json) {
            combined.push(...wrapAsEnvelope(s.sectionName, json));
          }
        });
        return combined;
      }

      // Fallback to realtime sections
      if (scribeSections.length > 0) {
        scribeSections.forEach((section) => {
          const content = getSectionContent(section.id);
          if (content?.jsonContent) {
            combined.push(
              ...wrapAsEnvelope(section.section_name, content.jsonContent)
            );
          }
        });
      }

      return combined;
    }, [
      apiSections,
      apiSectionContents,
      scribeSections,
      getSectionContent,
      wrapAsEnvelope,
    ]);

  // Centralized Accept handler
  const handleAccept = useCallback(
    async (
      params: {
        sectionName: string;
        jsonContent?: unknown;
        textContent?: string;
      },
      debugContext?: Record<string, unknown>
    ) => {
      const endpoint = getEndpointForAction('post-scribe-data');
      const appointmentId = selectedAppointment?.appointmentId;

      console.groupCollapsed(
        `📤 Accept clicked: ${params.sectionName} -> post-scribe-data`
      );
      console.log('Context:', {
        sectionName: params.sectionName,
        hasJson: params.jsonContent != null,
        hasText: !!params.textContent,
        appointmentId,
        selectedLocationId,
        hasValidToken,
        endpoint,
        debugContext,
      });

      try {
        if (!endpoint) {
          throw new Error(
            'Integration endpoint not found for post-scribe-data'
          );
        }
        if (!hasValidToken || !integrationToken) {
          throw new Error('Missing integration token');
        }
        if (!selectedLocationId) {
          throw new Error('No location selected');
        }
        if (!appointmentId) {
          throw new Error('No appointment selected');
        }

        const payload =
          params.jsonContent != null
            ? params.jsonContent
            : {
                sectionName: params.sectionName,
                textContent: params.textContent || '',
              };

        const jsonString = JSON.stringify(payload);

        const result = await makeCallToIntegrationPartner(
          endpoint.endpoint_url,
          endpoint.method,
          integrationToken.token,
          selectedLocationId,
          appointmentId,
          jsonString
        );

        console.log('Cloud function result:', result);
        if (result.success) {
          toast({
            title: 'Section sent',
            description: `${params.sectionName} accepted and sent to partner`,
          });
        } else {
          throw new Error(result.error || 'Unknown error');
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('Accept handling failed:', message);
        toast({
          title: 'Failed to send section',
          description: message,
        });
      } finally {
        console.groupEnd();
      }
    },
    [
      getEndpointForAction,
      hasValidToken,
      integrationToken,
      selectedAppointment?.appointmentId,
      selectedLocationId,
      makeCallToIntegrationPartner,
      toast,
    ]
  );

  // Bulk accept handler
  const handleAcceptAll = useCallback(async () => {
    try {
      setIsAcceptingAll(true);

      const combinedEnvelopes = collectAllSectionJsonEnvelopes();

      if (combinedEnvelopes.length === 0) {
        toast({
          title: 'Nothing to accept',
          description: 'No section JSON is available yet to send.',
        });
        return;
      }

      await handleAccept(
        {
          sectionName: 'All Sections',
          jsonContent: combinedEnvelopes,
        },
        {
          source: apiSections.length > 0 ? 'bulk-apiSections' : 'bulk-realtime',
          totalObjects: combinedEnvelopes.length,
          apiSectionsCount: apiSections.length,
          realtimeSectionNames: scribeSections.length,
        }
      );
    } finally {
      setIsAcceptingAll(false);
    }
  }, [
    collectAllSectionJsonEnvelopes,
    handleAccept,
    apiSections.length,
    scribeSections.length,
    toast,
  ]);

  // Function to fetch content from API section originalJsonKey
  const fetchApiSectionContent = useCallback(
    async (section: AiScribeJobSection) => {
      if (!section.originalJsonKey || !section.sectionName) return;
      const sectionKey = `${section.sectionName}-${section.id}`;

      // Add this check to prevent duplicate requests
      if (fetchingSectionsRef.current.has(sectionKey)) {
        return;
      }

      // Mark as being fetched
      fetchingSectionsRef.current.add(sectionKey);

      if (
        apiSectionContents[sectionKey]?.content ||
        apiSectionContents[sectionKey]?.loading
      ) {
        return;
      }

      try {
        const response = await fetch(section.originalJsonKey);
        if (!response.ok)
          throw new Error(`Failed to fetch: ${response.status}`);
        const content = await response.text();
        let jsonContent = null;
        try {
          jsonContent = JSON.parse(content);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          console.error(error.stack);
          toast({
            title: `Failed to parse JSON for ${section.sectionName}`,
            variant: 'destructive',
            duration: 10000,
            description: `${errorMessage}`,
          });
        }

        setApiSectionContents((prev) => ({
          ...prev,
          [sectionKey]: {
            ...prev[sectionKey],
            content,
            jsonContent,
            loading: false,
          },
        }));
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        setApiSectionContents((prev) => ({
          ...prev,
          [sectionKey]: {
            ...prev[sectionKey],
            loading: false,
            error: errorMessage,
          },
        }));
      } finally {
        fetchingSectionsRef.current.delete(sectionKey);
      }
    },
    []
  );

  // Auto-fetch content for API sections when they become available
  useEffect(() => {
    if (realtimeEnabled && apiSections.length === 0) {
      return;
    }

    const sectionsToLoad = apiSections.filter((section) => {
      const sectionKey = `${section.sectionName}-${section.id}`;
      return !apiSectionContents[sectionKey];
    });

    if (sectionsToLoad.length === 0) {
      return;
    }

    setApiSectionContents((prev) => {
      const newState = { ...prev };
      sectionsToLoad.forEach((section) => {
        const sectionKey = `${section.sectionName}-${section.id}`;
        newState[sectionKey] = {
          sectionName: section.sectionName,
          content: '',
          jsonContent: null,
          loading: true,
          error: null,
        };
      });
      return newState;
    });

    sectionsToLoad.forEach((section) => {
      fetchApiSectionContent(section);
    });
  }, [
    apiSections,
    realtimeEnabled,
    apiSectionContents,
    fetchApiSectionContent,
  ]);

  // EFFECT to clean up state when the appointment changes, preventing data leakage
  useEffect(() => {
    setApiSectionContents({});
    clearAllContent();
    // Reset success dialog states when appointment changes
    setShowSuccessDialog(false);
    setHasShownSuccessDialog(false);
  }, [selectedAppointment?.appointmentId, clearAllContent]);

  // Function to update JSON content for a specific section
  const updateSectionJsonContent = useCallback(
    async (sectionKey: string, updatedJsonContent: any) => {
      try {
        // Update local state optimistically
        setApiSectionContents((prev) => ({
          ...prev,
          [sectionKey]: {
            ...prev[sectionKey],
            jsonContent: updatedJsonContent,
            content: JSON.stringify(updatedJsonContent, null, 2), // Update text content as well
          },
        }));

        // Here you would typically make an API call to persist the changes
        // For now, we'll just store it locally
        console.log(
          `✅ Updated section "${sectionKey}" content:`,
          updatedJsonContent
        );

        // TODO: Implement API call to persist changes to the server
        // await updateSectionOnServer(sectionKey, updatedJsonContent);
      } catch (error) {
        console.error(`❌ Failed to update section "${sectionKey}":`, error);
        throw error;
      }
    },
    []
  );

  // Helper function to extract segmentIds from insight section data
  const extractSegmentIds = useCallback((jsonContent: any): string[] => {
    if (!jsonContent) return [];

    const rawIds: Array<string | number> = [];

    const isLikelySegmentId = (val: unknown): val is string => {
      if (val == null) return false;
      const s = String(val).trim();
      if (s.length === 0) return false;
      // Exclude anything containing whitespace (filters out sentences)
      if (/\s/.test(s)) return false;
      // Length bounds to avoid absurd tokens
      if (s.length < 2 || s.length > 64) return false;
      // Disallow dots to avoid ICD-10 codes like H35.39
      if (s.includes('.')) return false;
      // Require at least one digit or a dash/underscore to resemble an id
      if (!(/[0-9]/.test(s) || /[-_]/.test(s))) return false;
      // Exclude obvious ICD-10 pattern (e.g., H35.39, M16.5, S06.0X0)
      const icd10Regex = /^[A-TV-Z][0-9][0-9][A-Z0-9]?\.[A-Z0-9]{1,4}$/i;
      if (icd10Regex.test(s)) return false;
      // Allow only common id characters (no dot)
      if (!/^[A-Za-z0-9_:-]+$/.test(s)) return false;
      return true;
    };

    // Helper function to recursively search for segmentIds
    const searchForSegmentIds = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;

      // Check if this object has segmentIds
      if (Array.isArray(obj.segmentIds)) {
        console.log('🔍 Found segmentIds array:', {
          segmentIds: obj.segmentIds,
          segmentIdsType: typeof obj.segmentIds,
          segmentIdsLength: obj.segmentIds.length,
          firstSegmentId: obj.segmentIds[0],
          firstSegmentIdType: typeof obj.segmentIds[0],
          allSegmentIds: obj.segmentIds,
        });
        obj.segmentIds.forEach((v: any) => rawIds.push(v));
      }

      // Recursively search through all properties
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];
          if (Array.isArray(value)) {
            value.forEach((item) => searchForSegmentIds(item));
          } else if (typeof value === 'object') {
            searchForSegmentIds(value);
          }
        }
      }
    };

    console.log('🔍 Starting segmentIds extraction from:', {
      jsonContentType: typeof jsonContent,
      jsonContentKeys: Object.keys(jsonContent || {}),
      jsonContentPreview: JSON.stringify(jsonContent).substring(0, 200) + '...',
    });

    searchForSegmentIds(jsonContent);

    // Normalize, filter and deduplicate
    const filtered = rawIds.map((v) => String(v)).filter(isLikelySegmentId);
    const seen = new Set<string>();
    const segmentIds = filtered.filter((id) => {
      const key = id.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    console.log('🔍 Extracted segmentIds:', {
      segmentIds,
      count: segmentIds.length,
      types: segmentIds.map((id) => typeof id),
      sampleIds: segmentIds.slice(0, 5),
    });

    return segmentIds;
  }, []);

  // Handler for section clicks to highlight segments
  const handleSectionClick = useCallback(
    (sectionName: string, jsonContent: any, event?: React.MouseEvent) => {
      // Prevent event bubbling to avoid clearing highlights
      if (event) {
        event.stopPropagation();
      }

      console.log(`🎯 Section "${sectionName}" clicked:`, {
        hasJsonContent: !!jsonContent,
        jsonContentType: typeof jsonContent,
        jsonContentKeys: jsonContent ? Object.keys(jsonContent) : [],
        hasOnHighlightSegments: !!onHighlightSegments,
        hasOnSelectedSection: !!onSelectedSection,
      });

      if (!onHighlightSegments || !jsonContent) {
        console.log(`⚠️ Cannot highlight:`, {
          hasOnHighlightSegments: !!onHighlightSegments,
          hasJsonContent: !!jsonContent,
        });
        return;
      }

      const segmentIds = extractSegmentIds(jsonContent);
      console.log(`🔍 Extracted segmentIds for "${sectionName}":`, {
        segmentIds,
        count: segmentIds.length,
        jsonContentPreview:
          JSON.stringify(jsonContent).substring(0, 200) + '...',
      });

      if (segmentIds.length > 0) {
        console.log(
          `🎯 Highlighting ${segmentIds.length} segments for section "${sectionName}":`,
          segmentIds
        );
        onSelectedSection(sectionName);
        onHighlightSegments(segmentIds);

        // Check if transcript is visible and provide guidance if not
        const transcriptContainer = document.querySelector(
          '[data-transcript-container]'
        );
        if (!transcriptContainer) {
          setTimeout(() => {
            toast({
              title: 'Transcript not visible',
              description:
                'Open the transcript panel to see highlighted segments',
            });
          }, 500);
        }
      } else {
        console.log(`ℹ️ No segmentIds found for section "${sectionName}"`);
      }
    },
    [onHighlightSegments, extractSegmentIds, toast, onSelectedSection]
  );

  const renderEhrDocumentationContent = () => {
    // --- MODE 1: LIVE REALTIME MODE ---

    if (realtimeEnabled) {
      if (isRealtimeLoading && scribeSections.length === 0) {
        return (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-6">
              {/* Realtime connection animation */}
              <div className="relative">
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.7, 1, 0.7],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="w-16 h-16 bg-gradient-to-br from-eyevantage-primary/20 to-purple-500/20 rounded-3xl flex items-center justify-center border border-white/30 dark:border-white/20 backdrop-blur-xl">
                  <motion.div
                    animate={{
                      rotate: [0, 360],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                    className="w-8 h-8 border-2 border-slate-200 dark:border-slate-700 border-t-eyevantage-primary rounded-full"
                  />
                </motion.div>

                <div className="absolute inset-0">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 bg-green-400 rounded-full animate-pulse"
                      style={{
                        left: `${
                          50 + 30 * Math.cos((i * 120 * Math.PI) / 180)
                        }%`,
                        top: `${
                          50 + 30 * Math.sin((i * 120 * Math.PI) / 180)
                        }%`,
                        animationDelay: `${i * 0.6}s`,
                      }}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="text-slate-700 dark:text-slate-300 font-medium">
                  Connecting to realtime updates...
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="text-slate-500 dark:text-slate-400 text-sm">
                  Establishing live connection for real-time EHR documentation
                </motion.p>
              </div>
            </div>
          </div>
        );
      }

      if (scribeSections.length > 0) {
        return sortSectionsByOrder(
          scribeSections.map((section: ScribeSection) => ({
            ...section,
            sectionName: mapBackendToDisplayName(section.section_name),
            jsonContent: getSectionContent(section.id)?.jsonContent || null,
          }))
        ).map((section: ScribeSection | any, index: number) => {
          const sectionKey = `${section.section_name}-${section.id}`;
          const sectionContent: ScribeSectionContent = getSectionContent(
            section.id
          );
          const sectionType = getSectionType(
            section.sectionName,
            sectionContent?.jsonContent
          );
          const RealtimeSectionComponent = sectionType
            ? SECTION_ORDER_CONFIG.components[sectionType]
            : null;

          if (RealtimeSectionComponent) {
            return (
              <motion.div
                key={sectionKey}
                className={`break-inside-avoid max-w-full ${
                  selectedSection === section.sectionName
                    ? 'ring-2 ring-purple-500/80 dark:ring-purple-500/80 shadow-md shadow-purple-200/50 dark:shadow-purple-900/20 rounded-2xl'
                    : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: index * 0.1,
                  duration: 0.6,
                  ease: 'easeOut',
                }}>
                <div
                  onClick={(e) =>
                    handleSectionClick(
                      section.sectionName,
                      section.jsonContent,
                      e
                    )
                  }
                  className={`relative ${
                    onHighlightSegments ? 'cursor-pointer' : ''
                  }`}>
                  <RealtimeSectionComponent
                    isLoading={sectionContent?.loading || false}
                    jsonContent={sectionContent.jsonContent || null}
                    error={sectionContent?.error || null}
                    index={index}
                    onUpdate={async (updatedData) => {
                      await updateSectionJsonContent(section.id, updatedData);
                    }}
                    onAccept={() =>
                      handleAccept(
                        {
                          sectionName: sectionType || section.sectionName,
                          jsonContent: section.jsonContent,
                        },
                        { source: 'realtime', id: section.id }
                      )
                    }
                    onHighlightSegments={(segmentIds) => {
                      handleSectionClick(section.sectionName, {
                        ...section.jsonContent,
                        segmentIds,
                      });
                    }}
                  />
                </div>
              </motion.div>
            );
          }
          return (
            <SectionCard
              key={section.id}
              sectionName={section.sectionName}
              content={
                sectionContent?.textContent ||
                (section.jsonContent
                  ? JSON.stringify(section.jsonContent, null, 2)
                  : '')
              }
              isLoading={sectionContent?.loading || false}
              error={sectionContent?.error || null}
              index={index}
              onAccept={() =>
                handleAccept({
                  sectionName: section.sectionName,
                  textContent: sectionContent?.textContent,
                })
              }
              onClick={(e) =>
                handleSectionClick(section.sectionName, section.jsonContent, e)
              }
            />
          );
        });
      }
      return <AiProcessingLoader />;
    }

    // Show a sophisticated loader while fetching static content
    if (aiScribeLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="text-center space-y-6">
            <div className="space-y-2">
              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-slate-700 dark:text-slate-300 font-medium">
                Loading sections...
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="text-slate-500 dark:text-slate-400 text-sm">
                Fetching EHR documentation
              </motion.p>
            </div>
          </div>
        </div>
      );
    }

    if (apiSections.length > 0) {
      return sortSectionsByOrder(
        apiSections.map((section) => ({
          ...section,
          sectionName: section.sectionName,
          jsonContent:
            apiSectionContents[`${section.sectionName}-${section.id}`]
              ?.jsonContent || null,
        }))
      ).map((section, index) => {
        const sectionKey = `${section.sectionName}-${section.id}`;
        const sectionContent = apiSectionContents[sectionKey];
        const sectionType = getSectionType(
          section.sectionName,
          section.jsonContent
        );
        const NewSectionComponent = sectionType
          ? SECTION_ORDER_CONFIG.components[sectionType]
          : null;
        const assessmentPlanAllContents: any[] = [];
        apiSections.forEach((s) => {
          const key = `${s.sectionName}-${s.id}`;
          const content = apiSectionContents[key]?.jsonContent || null;
          const type = getSectionType(s.sectionName, content || null);
          if (type === 'Assessment & Plan' && content) {
            assessmentPlanAllContents.push(content);
          }
        });

        let renderedAssessmentPlan = false;
        if (NewSectionComponent) {
          // Aggregate Assessment & Plan into a single render
          if (sectionType === 'Assessment & Plan') {
            if (renderedAssessmentPlan) {
              return null; // skip duplicates
            }
            renderedAssessmentPlan = true;
            const aggregatedJson =
              assessmentPlanAllContents.length > 0
                ? assessmentPlanAllContents.flat()
                : sectionContent?.jsonContent || null;
            return (
              <motion.div
                key={sectionKey}
                className={`break-inside-avoid max-w-full mb-6 ${
                  selectedSection === section.sectionName
                    ? 'ring-2 ring-purple-500/80 dark:ring-purple-500/80 shadow-md shadow-purple-200/50 dark:shadow-purple-900/20 rounded-2xl'
                    : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: index * 0.1,
                  duration: 0.6,
                  ease: 'easeOut',
                }}>
                <div
                  onClick={(e) =>
                    handleSectionClick(section.sectionName, aggregatedJson, e)
                  }
                  className={`relative ${
                    onHighlightSegments ? 'cursor-pointer' : ''
                  }`}>
                  <NewSectionComponent
                    jsonContent={aggregatedJson}
                    isLoading={sectionContent?.loading || false}
                    error={sectionContent?.error || null}
                    index={index}
                    onUpdate={(updatedData) =>
                      updateSectionJsonContent(sectionKey, updatedData)
                    }
                    onAccept={() =>
                      handleAccept(
                        {
                          sectionName: sectionType || section.sectionName,
                          jsonContent: aggregatedJson,
                        },
                        {
                          source: 'apiSections',
                          sectionKey,
                        }
                      )
                    }
                    onHighlightSegments={(segmentIds) => {
                      handleSectionClick(section.sectionName, {
                        ...(aggregatedJson || {}),
                        segmentIds,
                      });
                    }}
                  />
                </div>
              </motion.div>
            );
          }

          // Special handling for CC & HPI section to support individual field highlighting
          if (sectionType === 'CC & HPI') {
            return (
              <motion.div
                key={sectionKey}
                className={`break-inside-avoid max-w-full mb-6 ${
                  selectedSection === section.sectionName
                    ? 'ring-2 ring-purple-500/80 dark:ring-purple-500/80 shadow-md shadow-purple-200/50 dark:shadow-purple-900/20 rounded-2xl'
                    : ''
                }`}>
                <NewSectionComponent
                  jsonContent={sectionContent?.jsonContent || null}
                  isLoading={sectionContent?.loading || false}
                  error={sectionContent?.error || null}
                  index={index}
                  onUpdate={(updatedData) =>
                    updateSectionJsonContent(sectionKey, updatedData)
                  }
                  onAccept={() =>
                    handleAccept(
                      {
                        sectionName: sectionType || section.sectionName,
                        jsonContent: sectionContent?.jsonContent || null,
                      },
                      {
                        source: 'apiSections',
                        sectionKey,
                      }
                    )
                  }
                  onHighlightSegments={(segmentIds) => {
                    handleSectionClick(section.sectionName, {
                      ...sectionContent?.jsonContent,
                      segmentIds,
                    });
                  }}
                />
              </motion.div>
            );
          }

          // Default rendering for other specialized components
          return (
            <motion.div
              key={sectionKey}
              className={`break-inside-avoid max-w-full mb-6 ${
                selectedSection === section.sectionName
                  ? 'ring-2 ring-purple-500/80 dark:ring-purple-500/80 shadow-md shadow-purple-200/50 dark:shadow-purple-900/20 rounded-2xl'
                  : ''
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: index * 0.1,
                duration: 0.6,
                ease: 'easeOut',
              }}>
              <div
                onClick={(e) =>
                  handleSectionClick(
                    section.sectionName,
                    sectionContent?.jsonContent,
                    e
                  )
                }
                className={`relative ${
                  onHighlightSegments ? 'cursor-pointer' : ''
                }`}>
                <NewSectionComponent
                  jsonContent={sectionContent?.jsonContent || null}
                  isLoading={sectionContent?.loading || false}
                  error={sectionContent?.error || null}
                  index={index}
                  onUpdate={(updatedData) =>
                    updateSectionJsonContent(sectionKey, updatedData)
                  }
                  onAccept={() =>
                    handleAccept(
                      {
                        sectionName: sectionType || section.sectionName,
                        jsonContent: sectionContent?.jsonContent || null,
                      },
                      {
                        source: 'apiSections',
                        sectionKey,
                      }
                    )
                  }
                />
              </div>
            </motion.div>
          );
        }
      });
    }

    // Fallbacks
    if (selectedAppointment) {
      return <NoInsightsPlaceholder />;
    }

    // Default state: no appointment selected at all
    return null;
  };

  return (
    <div
      className="w-full h-full"
      onClick={(e) => {
        // Clear highlights when clicking on the container (outside of sections)
        if (e.target === e.currentTarget && onHighlightSegments) {
          onHighlightSegments([]);
        }
      }}>
      <GlassCard className="h-full" variant="elevated">
        <div
          className="flex flex-col h-full"
          style={{ height: 'calc(100vh - 17rem)' }}>
          {/* Fixed Header */}
          <div className="flex-shrink-0 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 tracking-wide">
                  EHR Documentation
                </h2>
                {realtimeEnabled && (
                  <div className="flex items-center gap-2 text-md">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        allExpectedSectionsLoaded
                          ? 'bg-green-500'
                          : 'bg-blue-500 animate-pulse'
                      }`}></div>
                    <span className="text-slate-600 dark:text-slate-400">
                      Live ({loadedSectionsCount}/{expectedSectionsCount})
                    </span>
                  </div>
                )}
              </div>
              {/* Only show Accept All button when there's actual data available */}
              {hasAnyContent && (
                <Button
                  aria-label="Accept all sections"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAcceptAll();
                  }}
                  size="sm"
                  disabled={isAcceptingAll}
                  className={`h-8 px-3 bg-gradient-to-r from-emerald-500/80 to-emerald-600/80 hover:from-emerald-500/90 hover:to-emerald-600/90 border border-emerald-400/30 text-white rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm ${
                    isAcceptingAll
                      ? 'opacity-90 cursor-not-allowed'
                      : 'hover:from-emerald-500/90 hover:to-emerald-600/90'
                  }`}>
                  {isAcceptingAll ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <CheckCircle className="w-3 h-3" />
                  )}
                  Accept All
                </Button>
              )}
            </div>
          </div>

          {/* Scrollable Content with Responsive Layout */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <motion.div
              layout
              className={`p-4 w-full max-w-full ${
                isTranscriptVisible
                  ? 'grid grid-cols-1 gap-6'
                  : 'grid grid-cols-1 md:grid-cols-2 gap-6'
              }`}
              initial={{ scale: 0.98, opacity: 0.8 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 40,
                mass: 0.6,
                duration: 0.3,
                layout: {
                  type: 'spring',
                  stiffness: 350,
                  damping: 38,
                  mass: 0.7,
                  duration: 0.4,
                },
              }}>
              {renderEhrDocumentationContent()}
            </motion.div>
          </div>
        </div>
      </GlassCard>
      {/* Success Dialog - Only shown for realtime sections when AI generates EHR documentation */}
      <SuccessDialog
        isOpen={showSuccessDialog}
        onClose={() => {}}
        title="All Done!"
        description=""
        autoCloseSeconds={2}
      />
    </div>
  );
};

export default AiScribeInsights;
